import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Product } from "../../types/products-module-types";
import { sortDataBy } from "../../../SharedModule/utils/dataSort";

type ProductsState = {
  list: Product[];
  orderedBy: {
    orderBy: string;
    criteria: "asc" | "desc";
    dataType?: "string";
  };
  isLoadingProducts: boolean;
  isLoadedProducts: boolean;
  needRefreshProducts: boolean;
  isShowingActives: boolean;
  isLoadingExternalBilling: boolean;
  isLoadingExternalProducts: boolean;
};

const initialState: ProductsState = {
  list: [],
  orderedBy: {
    orderBy: "name",
    criteria: "asc",
  },
  isLoadingProducts: true,
  isLoadedProducts: false,
  needRefreshProducts: false,
  isShowingActives: true,
  isLoadingExternalBilling: false,
  isLoadingExternalProducts: false,
};

export const productsSlice = createSlice({
  name: "product",
  initialState,
  reducers: {
    load: (state, action) => {
      state.list = action.payload;
      state.isLoadingProducts = false;
      state.isLoadedProducts = true;
      state.needRefreshProducts = false;
    },
    setIsLoadingProducts: (state, action) => {
      state.isLoadingProducts = action.payload;
    },
    setNeedRefreshProducts: (state, action) => {
      state.needRefreshProducts = action.payload;
    },
    startLoadingExternalBilling: (state) => {
      state.isLoadingExternalBilling = true;
    },
    setIsLoadingExternalBilling: (state, action) => {
      state.isLoadingExternalBilling = action.payload;
    },
    startLoadingExternalProducts: (state) => {
      state.isLoadingExternalProducts = true;
    },
    setIsLoadingExternalProducts: (state, action) => {
      state.isLoadingExternalProducts = action.payload;
    },
    setShowActives: (state, action) => {
      state.isShowingActives = action.payload;
    },
    changeOrder: (
      state,
      action: PayloadAction<{
        orderBy: string;
        dataType?: "string";
      }>
    ) => {
      const newOrderedBy: any = {
        ...action.payload,
        criteria: "asc",
      };
      if (newOrderedBy.orderBy === state.orderedBy.orderBy) {
        newOrderedBy.criteria =
          state.orderedBy.criteria === "asc" ? "desc" : "asc";
      }
      return { ...state, orderedBy: newOrderedBy };
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  load,
  setIsLoadingProducts,
  setNeedRefreshProducts,
  startLoadingExternalBilling,
  setIsLoadingExternalBilling,
  startLoadingExternalProducts,
  setIsLoadingExternalProducts,
  setShowActives,
  changeOrder,
} = productsSlice.actions;

export const IsLoadingProducts = (state: any) => {
  return state.products.isLoadingProducts;
};

export const IsLoadedProducts = (state: any) => {
  return state.products.isLoadedProducts;
};

export const SelectNeedRefreshProducts = (state: any) => {
  return state.products.needRefreshProducts;
};

export const IsLoadingExternalBilling = (state: any) => {
  return state.products.isLoadingExternalBilling;
};

export const IsLoadingExternalProducts = (state: any) => {
  return state.products.isLoadingExternalProducts;
};

export const IsShowingActives = (state: any) => {
  return state.products.isShowingActives;
};

export const SelectProducts = ({ products }: { products: ProductsState }) => {
  return { list: products.list };
};

export const SelectOrderCriteria = ({
  products,
}: {
  products: ProductsState;
}) => products.orderedBy;

export const SelectOrderedBy = createSelector(
  [SelectProducts, SelectOrderCriteria],
  (product, orderCriteria) => {
    return {
      list:
        (product.list &&
          sortDataBy(
            product.list,
            orderCriteria.orderBy,
            orderCriteria.criteria,
            orderCriteria.dataType
          )) ||
        [],
    };
  }
);

export default productsSlice.reducer;
